import { cn } from '@invent/wl-ui-kit-next';
import * as React from 'react';
import { Link } from 'react-router-dom';

import { ApiError, ProblemType, errorMessage } from '@/domains/api';
import { CATALOGUE_ROUTE } from '../constants';
import type { RegistrationStart, RegistrationSurveyCode } from '../types';
import { SEVERAL_PERSONS } from './registration-copy';
import { HelpLink, StepHeading } from './step-parts';

/**
 * What a signed-in account adds (SR-487 ruling 11): one of its own numbers — a person's run, which
 * adds the number to the person the account is (PFR §2.6) — or a firm's or a team's survey run.
 *
 * A choice has to be asked for because nothing derives it: a CRD alone cannot tell the
 * registrant's own from their lead advisor's, a team's anchor. Each hint is one line of what the
 * choice asks for (the owner's Q13). FA and FSP are not chosen by their surveys any more: a number
 * offers its survey on the list (PFR §3).
 */
const CHOICES: { key: string; start: RegistrationStart; label: string; hint: string }[] = [
  {
    key: 'crd',
    start: { numbers: ['crd'] },
    label: 'My CRD',
    hint: 'You will need your CRD number.'
  },
  {
    key: 'insurance',
    start: { numbers: ['insurance'] },
    label: 'My NPN or licence number',
    hint: 'You will need your NPN, or your licence number and its state.'
  },
  {
    key: 'RIA_FIRM',
    start: { survey: 'RIA_FIRM' },
    label: 'Start a firm survey',
    hint: 'You will need the firm’s SEC number.'
  },
  {
    key: 'TEAMS',
    start: { survey: 'TEAMS' },
    label: 'Start a team survey',
    hint: 'You will need your lead advisor’s CRD number.'
  }
];

/** What a link's `?survey=` starts signed in (ruling 11): a firm's or a team's run, or for FA
 *  and FSP the matching number — null for a value that names no registration. */
export function linkedStart(value: Nullable<string>): Nullable<RegistrationStart> {
  switch (value) {
    case 'FA':
      return { numbers: ['crd'] };
    case 'FSP':
      return { numbers: ['insurance'] };
    case 'RIA_FIRM':
    case 'TEAMS':
      return { survey: value satisfies RegistrationSurveyCode };
    default:
      return null;
  }
}

/** What a start that failed says. A 404 is survey-api's one answer for "there is no
 *  registration here" — no catalogue entry for the survey — and it is told apart from the rest
 *  only because it is the one a retry will not fix. No cycle is asked about: registration opens
 *  no response, so a registration is open whether or not the survey is. An account that is
 *  several persons cannot add a number to any one of them (`several-persons`, PFR D16): staff
 *  add it, and the Help link is the way to them (Q12). */
export function startFailure(error: unknown): React.ReactNode {
  if (error instanceof ApiError && error.isProblem(ProblemType.severalPersons)) {
    return <SeveralPersons />;
  }
  if (error instanceof ApiError && error.status === 404) {
    return 'Registration for that survey is not open at the moment.';
  }
  return errorMessage(error, 'Could not start the registration.');
}

/** `409 several-persons`, at the start or the completion of a number signed in (Q12). */
function SeveralPersons() {
  return (
    <>
      {SEVERAL_PERSONS} <HelpLink />
    </>
  );
}

/** "What would you like to add?" — signed in, the screen before any run exists, so the only
 *  thing on it that talks to the server is the choice itself. The anonymous door asks for numbers
 *  instead (`ChooseNumbers`). */
export function ChooseRegistration({
  onChoose
}: {
  onChoose: (start: RegistrationStart) => Promise<void>;
}) {
  const [starting, setStarting] = React.useState<Nullable<string>>(null);
  const [failure, setFailure] = React.useState<React.ReactNode>(null);

  const choose = async (key: string, start: RegistrationStart) => {
    setStarting(key);
    setFailure(null);
    try {
      await onChoose(start);
    } catch (error) {
      setFailure(startFailure(error));
      setStarting(null);
    }
  };

  return (
    <>
      <StepHeading title="What would you like to add?" />

      <div className="grid gap-3">
        {CHOICES.map((choice) => (
          <button
            key={choice.key}
            type="button"
            disabled={starting !== null}
            aria-busy={starting === choice.key}
            // Named by the label and described by the hint, rather than named by both as the
            // button's text content would be: the label is the answer, the hint is advice.
            aria-labelledby={`registration-label-${choice.key}`}
            aria-describedby={`registration-hint-${choice.key}`}
            onClick={() => void choose(choice.key, choice.start)}
            className={cn(
              'w-full rounded-md border border-border px-4 py-3 text-left text-sm',
              'transition-colors',
              'hover:border-[var(--color-ocean-blue)] hover:bg-[var(--color-slight-blue)]/30',
              'disabled:cursor-default disabled:opacity-60',
              starting === choice.key &&
                'border-[var(--color-sapphire)] bg-[var(--color-slight-blue)]/60'
            )}
          >
            <span
              id={`registration-label-${choice.key}`}
              className="font-medium text-[var(--color-royal-blue)]"
            >
              {choice.label}
            </span>
            <span
              id={`registration-hint-${choice.key}`}
              className="mt-0.5 block text-xs text-muted-foreground"
            >
              {choice.hint}
            </span>
          </button>
        ))}
      </div>

      {failure && (
        <p role="alert" className="mt-4 text-sm font-medium text-destructive">
          {failure}
        </p>
      )}

      <p className="mt-6 text-center text-sm text-muted-foreground">
        <Link to={CATALOGUE_ROUTE} className="hover:text-[var(--color-royal-blue)]">
          Back to your surveys
        </Link>
      </p>
    </>
  );
}
